/**
 * Used on any method to memoize it's returned value based on the first input
 * parameter, so that heavy executions aren't performed over and over.
 *
 * @param global If set to true, then the response will be cached globally
 *        regardless of the execution context of the memoized method.
 */
export const memoize = (global: boolean = false) => {
  return (target: {}, propertyKey: string, descriptor: PropertyDescriptor) => {
    // Create a symbol which we're going to extract a reference to the cache
    // object under the current execution context, if we're not caching by
    // instance
    const cacheSymbol = Symbol("@use/decorators.memoize.cache");

    // Initialize an internal memoization cache, if we're not going to cache
    // by instance
    let cache: Map<unknown, unknown>;

    if (global) {
      cache = new Map();
    }

    return {
      // Forward configurable and enumerable "as is"
      configurable: descriptor.configurable,
      enumerable: descriptor.enumerable,

      // Now override the method signature, to ensure that the returned
      // value is memoized!
      value(arg0?: unknown, ...args: unknown[]): any {
        // tslint:disable-line no-any
        // If we're meant to cache "by taget", then we need get the
        // cache map as stored on the target
        if (!global) {
          // @ts-expect-error
          cache = this[cacheSymbol] = this[cacheSymbol] || new Map();
        }

        // If the value hasn't been memoized yet, then do it now
        if (!cache.has(arg0)) {
          // tslint:disable-next-line no-invalid-this no-unsafe-any
          cache.set(arg0, descriptor.value.call(this, arg0, ...args));
        }

        return cache.get(arg0);
      },
    };
  };
};
